import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { format, addDays } from 'date-fns';
import apiClient, { getAuthHeaders, getStoredToken, withApiPath } from '../../utils/apiClient';
import { composeDockHoursRange, splitDockHoursRange } from '../../utils/time';
import { formatPhoneNumber } from '../../utils/phone';

const QuotesBookingsContext = createContext();

const trailerDefaults = {
  'Dry Van': { lengths: ["53'", "48'"], requirements: ['Logistics Posts', 'E-Track', 'Food Grade Trailer', 'Vented Trailer'] },
  Reefer: { lengths: ["53'", "48'"], requirements: ['Food Grade Trailer', 'Multi-Temp Zones', 'Download Temp Logs'] },
  Flatbed: {
    lengths: ["53'", "48'", "45'", "40'"],
    requirements: ['4ft Tarps', '6ft Tarps', '8ft Tarps', 'Chains', 'Dunnage', 'Coil Racks']
  },
  'Flatbed Hotshot': { lengths: ["40'", "35'", "30'", "26'", "20'"], requirements: ['Chains', 'Straps', 'Dunnage'] },
  Conestoga: { lengths: ["53'", "48'"], requirements: ['Full Enclosure', 'Liftgate'] },
  'Step Deck': { lengths: ["53'", "48'"], requirements: ['Ramp Kit', 'Chains', 'Dunnage'] },
  RGN: { lengths: ["53'", "48'"], requirements: ['Multi-Axle', 'Escort Required', 'Ramps'] },
  Lowboy: { lengths: ["48'", "29'", "24'"], requirements: ['Multi-Axle', 'Police Escort', 'Flag Kit'] },
  'Box Truck': { lengths: ["26'", "24'", "22'", "20'", "18'"], requirements: ['Liftgate', 'Pallet Jack', 'Logistics Posts'] },
  'Sprinter Van': { lengths: ["14'", "12'", "10'"], requirements: ['Load Bars', 'Insurance Waiver'] }
};

const defaultQuotes = [
  {
    id: 'Q-48251',
    createdOn: format(new Date(), 'MMM d, yyyy'),
    status: 'Awaiting Market Data',
    mode: 'FTL',
    equipment: 'Reefer',
    lane: 'Chicago, IL → Los Angeles, CA',
    pickupDate: format(addDays(new Date(), 2), 'MMM d'),
    deliveryDate: format(addDays(new Date(), 5), 'MMM d'),
    rateEstimate: null,
    stops: 2,
    notes: 'Perishables – maintain 34°F'
  },
  {
    id: 'Q-48219',
    createdOn: format(addDays(new Date(), -1), 'MMM d, yyyy'),
    status: 'Estimate Ready',
    mode: 'LTL',
    equipment: 'Box Truck',
    lane: 'Dallas, TX → Atlanta, GA',
    pickupDate: format(addDays(new Date(), 1), 'MMM d'),
    deliveryDate: format(addDays(new Date(), 3), 'MMM d'),
    rateEstimate: '$1,340',
    stops: 3,
    notes: 'Two liftgate deliveries'
  }
];

const defaultBookings = [
  {
    id: 'B-2094',
    bookedOn: format(addDays(new Date(), -2), 'MMM d, yyyy'),
    customerRef: 'PO-88341',
    mode: 'FTL',
    equipment: 'Dry Van',
    origin: 'Columbus, OH',
    destination: 'Miami, FL',
    value: '$3,870',
    carrierId: 'carrier-fl'
  },
  {
    id: 'B-2091',
    bookedOn: format(addDays(new Date(), -4), 'MMM d, yyyy'),
    customerRef: 'PO-77135',
    mode: 'LTL',
    equipment: 'Box Truck',
    origin: 'Austin, TX',
    destination: 'Denver, CO',
    value: '$1,120',
    carrierId: 'carrier-ae'
  }
];

export const QuotesBookingsProvider = ({ children }) => {
  const [quotes, setQuotes] = useState(defaultQuotes);
  const [bookings, setBookings] = useState(defaultBookings);
  const [carriers, setCarriers] = useState([]);
  const [isLoadingCarriers, setIsLoadingCarriers] = useState(false);
  const [carriersError, setCarriersError] = useState('');
  const [myLocations, setMyLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState('');

  const fuelIndex = useMemo(
    () => ({
      current: 4.32,
      weekChange: -0.04,
      description: 'National diesel index updated weekly by EIA.'
    }),
    []
  );

  const addQuote = (quote) => {
    setQuotes((prev) => [
      {
        id: `Q-${Math.floor(40000 + Math.random() * 5000)}`,
        createdOn: format(new Date(), 'MMM d, yyyy'),
        status: 'Awaiting Market Data',
        ...quote
      },
      ...prev
    ]);
  };

  const addBooking = (booking) => {
    setBookings((prev) => [
      {
        id: `B-${Math.floor(2000 + Math.random() * 800)}`,
        bookedOn: format(new Date(), 'MMM d, yyyy'),
        ...booking
      },
      ...prev
    ]);
  };

  const normalizeCarrier = useCallback((carrier) => ({
    id: String(carrier.id ?? carrier.carrier_id ?? carrier.identifier ?? carrier.mc_number ?? carrier.dot_number ?? ''),
    name: carrier.company_name || carrier.name || '',
    mc: carrier.mc_number || carrier.mc || '',
    dot: carrier.dot_number || carrier.dot || '',
    scac: carrier.scac_code || carrier.scac || '',
    rating: carrier.rating || carrier.carrier_performance_score || '',
    contacts: Array.isArray(carrier.contacts)
      ? carrier.contacts.map((contact) => ({
          id: contact.id || contact.contact_id || `${contact.name || 'contact'}-${Math.random().toString(36).slice(2, 8)}`,
          name: contact.name || '',
          role: contact.job_title || contact.role || contact.type || '',
          phone: formatPhoneNumber(contact.phone || ''),
          extension: contact.extension || contact.ext || '',
          email: contact.email || '',
        }))
      : [],
    dispatcher: {
      name: carrier.dispatcher_name || '',
      phone: carrier.dispatcher_phone || '',
      extension: carrier.dispatcher_extension || '',
      email: carrier.dispatcher_email || ''
    },
    driver: {
      name: carrier.driver_name || '',
      phone: carrier.driver_phone || '',
      vin: carrier.driver_vin || '',
      truck: carrier.truck_number || '',
      trailer: carrier.trailer_number || ''
    }
  }), []);

  const fetchCarriers = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setCarriers([]);
      return;
    }

    setIsLoadingCarriers(true);
    setCarriersError('');

    try {
      const response = await apiClient.get(withApiPath('/carriers/'), {
        headers: getAuthHeaders(token),
        params: { ordering: 'company_name' },
      });

      const payload = response.data?.results || response.data || [];
      const normalized = Array.isArray(payload) ? payload.map(normalizeCarrier) : [];
      setCarriers(normalized);
    } catch (error) {
      console.error('Failed to load carriers', error);
      setCarriersError('Unable to load carriers right now.');
      setCarriers([]);
    } finally {
      setIsLoadingCarriers(false);
    }
  }, [normalizeCarrier]);

  const normalizeLocation = useCallback((location) => {
    const range = splitDockHoursRange(location.dock_hours || location.dockHours || '');
    const dockHoursFrom = location.dock_hours_from || location.dockHoursFrom || range.from || '';
    const dockHoursTo = location.dock_hours_to || location.dockHoursTo || range.to || '';
    const contacts = (location.contacts || []).map((contact) => ({
      id: contact.id || contact.contact_id,
      name: contact.name || '',
      role: contact.role || '',
      phone: formatPhoneNumber(contact.phone || ''),
      ext: contact.ext || '',
      email: contact.email || '',
      notes: contact.notes || '',
    }));

    return {
      id: String(location.id ?? location.location_id ?? location.identifier ?? location.name),
      name: location.company_name || location.name || '',
      city: location.city || '',
      state: location.state || '',
      zip: location.zip || location.zip_code || '',
      street: location.address || location.street || '',
      phone: formatPhoneNumber(location.phone || location.phone_number || ''),
      email: location.email || location.contact_email || '',
      dockHours: location.dock_hours || location.dockHours || composeDockHoursRange(dockHoursFrom, dockHoursTo),
      dockHoursFrom,
      dockHoursTo,
      dockType: (location.dock_type ?? location.dockType)?.toUpperCase() || '',
      notes: location.notes || location.route_notes || '',
      contacts,
    };
  }, []);

  const fetchLocations = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setMyLocations([]);
      setAllLocations([]);
      return;
    }

    setIsLoadingLocations(true);
    setLocationsError('');
    try {
      const [myResponse, allResponse] = await Promise.all([
        apiClient.get(withApiPath('/locations/my/'), {
          headers: getAuthHeaders(token),
        }),
        apiClient.get(withApiPath('/locations/all/'), {
          headers: getAuthHeaders(token),
        }),
      ]);
      const myPayload = myResponse.data?.results || myResponse.data || [];
      const allPayload = allResponse.data?.results || allResponse.data || [];
      const normalizedMy = myPayload.map(normalizeLocation);
      const normalizedAll = allPayload.map(normalizeLocation);
      const mergedIds = new Set();
      const mergedAll = [...normalizedMy, ...normalizedAll].filter((loc) => {
        if (mergedIds.has(loc.id)) return false;
        mergedIds.add(loc.id);
        return true;
      });
      setMyLocations(normalizedMy);
      setAllLocations(mergedAll);
    } catch (error) {
      console.error('Failed to load locations', error);
      setLocationsError('Unable to load saved locations right now.');
      setMyLocations([]);
      setAllLocations([]);
    } finally {
      setIsLoadingLocations(false);
    }
  }, [normalizeLocation]);

  const addLocation = useCallback(
    async (location) => {
      const token = getStoredToken();

      if (!token) {
        throw new Error('Authentication is required to save a location.');
      }

      const dockTypeValue = location.dockType ? location.dockType.toUpperCase() : null;
      const primaryContact = (location.contacts || []).find((contact) => contact.name || contact.phone || contact.email);

      const dockHoursFrom = location.dockHoursFrom || '';
      const dockHoursTo = location.dockHoursTo || '';
      const payload = {
        company_name: location.name,
        address: location.street,
        city: location.city,
        state: location.state,
        zip: location.zip,
        phone: location.phone || primaryContact?.phone || '',
        email: location.email || primaryContact?.email || '',
        dock_type: dockTypeValue || null,
        dock_hours: location.dockHours || composeDockHoursRange(dockHoursFrom, dockHoursTo),
        dock_hours_from: dockHoursFrom,
        dock_hours_to: dockHoursTo,
        notes: location.notes || '',
        contacts: location.contacts || [],
      };

      const response = await apiClient.post(withApiPath('/locations/'), payload, {
        headers: getAuthHeaders(token),
      });
      const enrichedLocation = normalizeLocation(response.data);

      setMyLocations((prev) => [enrichedLocation, ...prev.filter((loc) => loc.id !== enrichedLocation.id)]);
      setAllLocations((prev) => [enrichedLocation, ...prev.filter((loc) => loc.id !== enrichedLocation.id)]);

      return enrichedLocation;
    },
    [normalizeLocation]
  );

  const detachLocationFromMy = useCallback(
    async (locationId) => {
      const token = getStoredToken();
      if (!token) {
        throw new Error('Authentication is required to remove a location.');
      }

      await apiClient.delete(withApiPath(`/locations/${locationId}/`), {
        headers: getAuthHeaders(token),
      });

      setMyLocations((prev) => prev.filter((loc) => loc.id !== String(locationId)));
    },
    []
  );

  const attachLocationToMy = useCallback(
    async (locationId) => {
      const token = getStoredToken();
      if (!token) {
        throw new Error('Authentication is required to attach a location.');
      }

      const response = await apiClient.post(withApiPath(`/locations/${locationId}/attach/`), null, {
        headers: getAuthHeaders(token),
      });

      const enrichedLocation = normalizeLocation(response.data);
      setMyLocations((prev) => [enrichedLocation, ...prev.filter((loc) => loc.id !== enrichedLocation.id)]);
      setAllLocations((prev) => [enrichedLocation, ...prev.filter((loc) => loc.id !== enrichedLocation.id)]);
      return enrichedLocation;
    },
    [normalizeLocation]
  );

  const removeLocationFromMy = useCallback(
    async (locationId) => {
      const token = getStoredToken();
      if (!token) {
        throw new Error('Authentication is required to remove a location.');
      }

      await apiClient.delete(withApiPath(`/locations/${locationId}/`), {
        headers: getAuthHeaders(token),
      });

      setMyLocations((prev) => prev.filter((loc) => loc.id !== String(locationId)));
    },
    []
  );

  useEffect(() => {
    fetchLocations();
    fetchCarriers();
  }, [fetchCarriers, fetchLocations]);

  const contextValue = useMemo(
    () => ({
      quotes,
      bookings,
      carriers,
      fuelIndex,
      trailerDefaults,
      myLocations,
      allLocations,
      isLoadingCarriers,
      isLoadingLocations,
      carriersError,
      locationsError,
      addQuote,
      addBooking,
      addLocation,
      removeLocationFromMy: detachLocationFromMy,
      attachLocationToMy,
    }),
    [
      quotes,
      bookings,
      fuelIndex,
      myLocations,
      allLocations,
      isLoadingCarriers,
      isLoadingLocations,
      carriersError,
      locationsError,
      addLocation,
      detachLocationFromMy,
      attachLocationToMy,
    ]
  );

  return <QuotesBookingsContext.Provider value={contextValue}>{children}</QuotesBookingsContext.Provider>;
};

export const useQuotesBookings = () => {
  const context = useContext(QuotesBookingsContext);
  if (!context) {
    throw new Error('useQuotesBookings must be used within a QuotesBookingsProvider');
  }
  return context;
};

export default QuotesBookingsContext;
